import CloseIcon from '@mui/icons-material/Close';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps,
  createFilterOptions,
  SxProps,
  Theme,
} from '@mui/material';
import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { Checkbox } from '~/components/Checkbox';
import { TextField } from '~/components/TextField';
import { useDebounce } from '~/hooks/useDebounce';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import { autocompleteOptionSchema } from '~/utils/formSchemas';

export type AutocompleteOption = Yup.InferType<typeof autocompleteOptionSchema>;

const filter = createFilterOptions<AutocompleteOption>();
const CREATABLE_OPTION_LABEL_PREFIX = 'Add new option';
const CHECKBOX_SX: SxProps<Theme> = { marginRight: '28px' };
const NO_INTERESTED_ICON_SX: SxProps<Theme> = { marginRight: '28px', color: 'var(--gray-2-color)' };
export const UNKNOWN_OPTION_VALUE = 'UNKNOWN';

interface Props<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  Creatable extends boolean | undefined,
> extends Omit<
    AutocompleteProps<AutocompleteOption, Multiple, DisableClearable, Creatable>,
    'renderInput' | 'onChange'
  > {
  multiple?: Multiple;
  placeholder: string;
  error?: boolean;
  helperText?: React.ReactNode;
  creatable?: Creatable;
  disableClearable?: DisableClearable;
  unknownOptionLabel?: string;
  creatableWithoutCheckbox?: boolean;
  creatableWithoutOptionLabelPrefix?: boolean;
  onChange?: (value: AutocompleteOption | AutocompleteOption[]) => void;
  onSearchValueChange?: (value: string) => void;
}

const Component = forwardRef(
  <
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    Creatable extends boolean | undefined,
  >(
    {
      multiple,
      placeholder,
      error,
      helperText,
      creatable,
      disableClearable,
      unknownOptionLabel,
      loading,
      options,
      creatableWithoutCheckbox,
      creatableWithoutOptionLabelPrefix,
      onChange,
      onSearchValueChange,
      ...props
    }: Props<Multiple, DisableClearable, Creatable>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): React.JSX.Element => {
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchValue = useDebounce(searchValue);

    useUpdateEffect(() => {
      onSearchValueChange?.(debouncedSearchValue);
    }, [debouncedSearchValue]);

    const removeCreatableOptionPrefixFromOption = useCallback(
      (option: AutocompleteOption): AutocompleteOption => {
        if (option.label?.startsWith(CREATABLE_OPTION_LABEL_PREFIX)) {
          return {
            ...option,
            label: option.label.slice(CREATABLE_OPTION_LABEL_PREFIX.length + 2, -1),
          };
        }

        return option;
      },
      [],
    );

    const unknownOption = useMemo<AutocompleteOption | undefined>(() => {
      if (unknownOptionLabel) {
        return { value: UNKNOWN_OPTION_VALUE, label: unknownOptionLabel };
      }

      return undefined;
    }, [unknownOptionLabel]);

    const allOptions = useMemo<AutocompleteOption[]>(() => {
      if (unknownOption) {
        return [unknownOption, ...options];
      }

      return [...options];
    }, [unknownOption, options]);

    const unknownOptionSelected = useMemo(() => {
      if (props.value) {
        const selectedOption = (
          Array.isArray(props.value) ? props.value[0] : props.value
        ) as AutocompleteOption;

        return selectedOption?.value === UNKNOWN_OPTION_VALUE;
      }

      return false;
    }, [props.value]);

    return (
      <MuiAutocomplete
        clearOnBlur
        disableClearable={disableClearable}
        disableCloseOnSelect={multiple}
        filterOptions={
          creatable
            ? (options, params) => {
                const filteredOptions = onSearchValueChange ? options : filter(options, params);
                const { inputValue } = params;
                const trimmedInputValue = inputValue.trim();

                const isExisting = options.some(option => trimmedInputValue === option.label);

                if (trimmedInputValue !== '' && !isExisting) {
                  filteredOptions.splice(unknownOptionLabel ? 1 : 0, 0, {
                    value: trimmedInputValue,
                    label: creatableWithoutOptionLabelPrefix
                      ? trimmedInputValue
                      : `${CREATABLE_OPTION_LABEL_PREFIX} "${trimmedInputValue}"`,
                  });
                }

                return filteredOptions;
              }
            : undefined
        }
        freeSolo={creatable}
        getOptionKey={option => (option as AutocompleteOption).value}
        getOptionLabel={
          creatable
            ? option =>
                option && removeCreatableOptionPrefixFromOption(option as AutocompleteOption).label
            : undefined
        }
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        loading={loading}
        multiple={multiple}
        options={allOptions}
        ref={ref}
        renderInput={params => (
          <TextField
            error={error}
            helperText={helperText}
            label={placeholder}
            loading={loading}
            variant="outlined"
            {...params}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={option.value}
            style={{
              ...props.style,
              ...(option.value === UNKNOWN_OPTION_VALUE && { color: 'var(--gray-2-color)' }),
            }}
          >
            {creatableWithoutCheckbox ? null : option.value === UNKNOWN_OPTION_VALUE ? (
              <NotInterestedIcon sx={NO_INTERESTED_ICON_SX} />
            ) : (
              multiple && <Checkbox checked={selected} sx={CHECKBOX_SX} />
            )}
            {option.label}
          </li>
        )}
        slotProps={{
          chip: {
            icon: unknownOptionSelected ? (
              <NotInterestedIcon
                sx={{
                  color: 'var(--gray-2-color)',
                  marginLeft: '0px !important',
                }}
              />
            ) : undefined,
            deleteIcon:
              props.disabled || unknownOptionSelected ? (
                <span style={{ display: 'none' }} />
              ) : (
                <CloseIcon
                  sx={{
                    color: 'var(--gray-7-color)',
                    width: '18px',
                    height: '18px',
                  }}
                />
              ),
            style: unknownOptionSelected
              ? {
                  fontWeight: '400',
                  border: 'none',
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: 'var(--gray-2-color)',
                }
              : undefined,
          },
        }}
        onChange={(event, newValue) => {
          if (newValue) {
            if (Array.isArray(newValue) && newValue.length) {
              const newOptions = newValue as AutocompleteOption[];

              if (newOptions[newOptions.length - 1].value === UNKNOWN_OPTION_VALUE) {
                // @ts-expect-error newValue is array
                newValue = [unknownOption];
              } else if (newOptions[0].value === UNKNOWN_OPTION_VALUE) {
                // @ts-expect-error newValue is array
                newValue = newOptions.slice(1);
              }
            }

            if (creatable) {
              onChange?.(
                Array.isArray(newValue)
                  ? newValue.map(option =>
                      removeCreatableOptionPrefixFromOption(option as AutocompleteOption),
                    )
                  : removeCreatableOptionPrefixFromOption(newValue as AutocompleteOption),
              );
            } else {
              onChange?.(newValue as AutocompleteOption | AutocompleteOption[]);
            }
          } else {
            // @ts-expect-error default value is null
            onChange?.(null);
          }
        }}
        onInputChange={(event, newValue) => setSearchValue(newValue)}
        {...props}
      />
    );
  },
);

export const Autocomplete = memo(Component);
